import React, { useState, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button, IconButton, Menu, MenuItem, Tooltip, CircularProgress } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useHistory } from 'react-router-dom';
import './DownloadInvoice.css';
import { ROUTES, PAPER_SIZE, INVOICE_LAYOUTS, PRINT_TYPES } from 'const';
import PrintIcon from '@mui/icons-material/Print';
import { useTranslation } from 'react-i18next';
import { ShopContext } from 'Context/ShopContext';
import { BillFormatContext } from 'Context/BillFormatContext';
import { TerminalContext } from 'Context/TerminalContext';
import { AlertMessagesContext } from 'react-alert-messages';
import SaleService from 'services/SaleService';
import TerminalServices from 'services/TerminalServices';
import PrintDocument from 'components/utils/PrintDocument/index';
import { STORAGE_KEYS, BACKEND_URL } from 'const';

const SaleInvoice = ({ bill, showOptions = true, renderAs = 'icon' }) => {
  const { t } = useTranslation('common');
  const { shop } = useContext(ShopContext);
  const { getBillFormat } = useContext(BillFormatContext);
  const { selectedTerminal } = useContext(TerminalContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [anchorEl, setAnchorEl] = useState(null);
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [saleToPrint, setSaleToPrint] = useState(null);
  const [billFormatToPrint, setBillFormatToPrint] = useState(null);
  const [totalsToPrint, setTotalsToPrint] = useState(null);
  const [terminalToPrint, setTerminalToPrint] = useState(null);
  const [paperSize, setPaperSize] = useState(PAPER_SIZE.A4);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const billToPdf = (bill, type) => {
    const authToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const shopId = localStorage.getItem(STORAGE_KEYS.SHOP_ID);

    if (!authToken || !shopId) {
      console.error('Missing auth token or shop ID');
      return;
    }

    if (!bill || !bill.bill_no) {
      console.error('Invalid bill object:', bill);
      return;
    }

    let url = `${BACKEND_URL}/reports/sales/details/pdf/?token=${authToken}&shop_id=${shopId}&bill_no=${bill.bill_no}`;
    if (type) {
      url += `&invoice_format=${type}`;
    }

    window.open(url);
  };
  const prepareInvoiceData = async (bill) => {
    let fullSale;
    if (bill.id) {
      fullSale = await SaleService.getSale(bill.id);
    } else if (bill.bill_no) {
      const sales = await SaleService.getSales({ bill_no: bill.bill_no });
      fullSale = Array.isArray(sales) ? sales[0] : sales;
    } else {
      console.error('Missing bill.id and bill.bill_no');
      return;
    }

    let parsedTerminalId =
      fullSale?.terminal?.id ||
      fullSale?.terminal_id ||
      fullSale?.initiated_terminal_id ||
      bill?.terminal_id ||
      bill?.initiated_terminal_id;

    if (!parsedTerminalId) {
      parsedTerminalId = selectedTerminal?.id;
    }

    let billFormat = null;
    if (parsedTerminalId) {
      billFormat = await getBillFormat(parsedTerminalId);
    }

    let terminal = selectedTerminal;
    if (parsedTerminalId) {
      try {
        terminal = await TerminalServices.getTerminal(parsedTerminalId);
      } catch (e) {
        console.error('Failed to fetch terminal details', e);
      }
    }
    const totals = {
      payable: Number(fullSale.payable || 0),
      tax: Number(fullSale.tax || 0),
      cess: Number(fullSale.cess || 0),
    };

    return { fullSale, billFormat, totals, terminal };
  };

  const handleDownloadInvoice = async (size) => {
    handleMenuClose();
    if (!bill || !bill.id) return;

    setIsLoading(true);
    try {
      const parsedSize =
        size === PAPER_SIZE.A4
          ? PAPER_SIZE.A4
          : size === PAPER_SIZE.A5_VERTICAL
          ? PAPER_SIZE.A5_VERTICAL
          : size === PAPER_SIZE.A5_HORIZONTAL
          ? PAPER_SIZE.A5_HORIZONTAL
          : PAPER_SIZE.THERMAL_80MM;
      setPaperSize(parsedSize);

      const { fullSale, billFormat, totals, terminal } = await prepareInvoiceData(bill);
      setSaleToPrint(fullSale);
      setBillFormatToPrint(billFormat);
      setTotalsToPrint(totals);
      setTerminalToPrint(terminal);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintFromSettings = async () => {
    handleMenuClose();
    if (!bill || (!bill.id && !bill.bill_no)) return;

    setIsLoading(true);

    try {
      const { fullSale, billFormat, totals, terminal } = await prepareInvoiceData(bill);

      const web_invoice_format = shop?.web_invoice_format;
      if (web_invoice_format === INVOICE_LAYOUTS.A4_BACKEND) {
        billToPdf(fullSale);
        return;
      } else if (web_invoice_format === INVOICE_LAYOUTS.A5_BACKEND) {
        billToPdf(fullSale, INVOICE_LAYOUTS.A5_BACKEND);
        return;
      }

      let selectedPaperSize = null;

      if (web_invoice_format === INVOICE_LAYOUTS.A5_PORTRAIT_FRONTEND) {
        selectedPaperSize = PAPER_SIZE.A5_VERTICAL;
      } else if (web_invoice_format === INVOICE_LAYOUTS.A5_LANDSCAPE_FRONTEND) {
        selectedPaperSize = PAPER_SIZE.A5_HORIZONTAL;
      } else if (web_invoice_format === INVOICE_LAYOUTS.A4_FRONTEND) {
        selectedPaperSize = PAPER_SIZE.A4;
      }

      setPaperSize(selectedPaperSize);
      setSaleToPrint(fullSale);
      setBillFormatToPrint(billFormat);
      setTotalsToPrint(totals);
      setTerminalToPrint(terminal);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (saleToPrint && totalsToPrint) {
      document.body.classList.add('printing-invoice');
      setTimeout(() => {
        window.print();
        document.body.classList.remove('printing-invoice');
        setSaleToPrint(null);
        setBillFormatToPrint(null);
        setTotalsToPrint(null);
        setTerminalToPrint(null);
      }, 500);
    }
  }, [saleToPrint, billFormatToPrint, totalsToPrint]);

  const handleEditRedirect = () => {
    const billId = bill?.id;

    if (!billId) {
      console.error('Bill ID is missing or invalid:', bill);
      return;
    }

    history.push(`${ROUTES.MANAGE_SALES}?id=${billId}`);
    handleMenuClose();
  };

  return (
    <div className="menu-container">
      {renderAs === 'button' ? (
        <Button
          variant="contained"
          color="primary"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
          onClick={() => handlePrintFromSettings()}
        >
          {t('Print')}
        </Button>
      ) : isLoading ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '38px',
            height: '38px',
            marginRight: '4px',
          }}
        >
          <CircularProgress size={20} />
        </div>
      ) : (
        <Tooltip title={t('Print Invoice')}>
          <IconButton onClick={() => handlePrintFromSettings()}>
            <PrintIcon className="iconBtn" />
          </IconButton>
        </Tooltip>
      )}

      {showOptions && (
        <div className="more-options-container">
          <Tooltip title={t('More options')}>
            <IconButton aria-label={t('More options')} onClick={handleMenuClick}>
              <MoreVertIcon className="iconBtn" />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            MenuListProps={{ className: 'menu-list' }}
          >
            <MenuItem onClick={() => handleDownloadInvoice(PAPER_SIZE.A4)} className="menu-item">
              {t('Invoice A4')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                billToPdf(bill, INVOICE_LAYOUTS.A4_BACKEND);
              }}
              className="menu-item"
            >
              {t('PDF A4')}
            </MenuItem>
            <MenuItem onClick={() => handleDownloadInvoice(PAPER_SIZE.A5_VERTICAL)} className="menu-item">
              {t('Invoice A5 (Portrait)')}
            </MenuItem>
            <MenuItem onClick={() => handleDownloadInvoice(PAPER_SIZE.A5_HORIZONTAL)} className="menu-item">
              {t('Invoice A5 (Landscape)')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                billToPdf(bill, INVOICE_LAYOUTS.A5_BACKEND);
              }}
              className="menu-item"
            >
              {t('PDF A5')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                billToPdf(bill, INVOICE_LAYOUTS.THREE_INCH);
              }}
              className="menu-item"
            >
              {t('Invoice 3 Inch')}
            </MenuItem>
            <MenuItem onClick={handleEditRedirect} className="menu-item">
              {t('Edit')}
            </MenuItem>
          </Menu>
        </div>
      )}

      {saleToPrint &&
        createPortal(
          <PrintDocument
            type={PRINT_TYPES.SALE}
            data={saleToPrint}
            shop={shop}
            billFormat={billFormatToPrint}
            totals={totalsToPrint}
            paperSize={paperSize}
            terminal={terminalToPrint}
          />,
          document.body
        )}
    </div>
  );
};

export default SaleInvoice;
